import React, { useState, useEffect } from 'react';
import { AppModal } from '../ModalSystem';
import { FaCamera } from 'react-icons/fa';
import api from '../../api';
import { notify } from '../CustomToast';

const ProfileModal = ({ isOpen, onClose, user, onUpdate }) => {
    const [formData, setFormData] = useState({
        name: '',
        user_type: '',
        password: '',
        password_confirmation: '',
    });
    const [avatar, setAvatar] = useState(null);
    const [preview, setPreview] = useState(null);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [isUploading, setIsUploading] = useState(false);
    const [tempUploadId, setTempUploadId] = useState(null);

    useEffect(() => {
        if (user && isOpen) {
            setFormData({
                name: user.name || '',
                user_type: user.user_type || 'CEO',
                password: '',
                password_confirmation: '',
            });
            if (user.avatar) {
                setPreview(user.avatar);
            } else {
                setPreview(null);
            }
            setErrors({});
            setAvatar(null);
            setTempUploadId(null);
        }
    }, [user, isOpen]);

    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 10 * 1024 * 1024) {
                notify('error', 'File is too large', 'Max size 10MB');
                return;
            }

            // Preview immediately
            setPreview(URL.createObjectURL(file));
            setAvatar(file);
            setIsUploading(true);

            try {
                const uploadData = new FormData();
                uploadData.append('file', file);

                const response = await api.post('/temp-upload', uploadData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });

                if (response.status) {
                    setTempUploadId(response.data.id);
                    notify('success', 'Image uploaded');
                }
            } catch (err) {
                notify('error', 'Failed to upload image');
                setPreview(user.avatar || null);
            } finally {
                setIsUploading(false);
            }
        }
    };

    const handleSubmit = async (e) => {
        if (e) e.preventDefault();
        setLoading(true);
        setErrors({});

        try {
            const response = await api.post('/profile/update', {
                ...formData,
                temp_upload_id: tempUploadId
            });

            if (response.status) {
                notify('success', 'Profile updated successfully');
                const updatedUser = response.data.user;
                localStorage.setItem('user', JSON.stringify(updatedUser));
                onUpdate(updatedUser);
                onClose();
            }
        } catch (err) {
            if (err.status === 422) {
                setErrors(err.errors);
            } else {
                notify('error', err.message || 'Failed to update profile');
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <AppModal
            isOpen={isOpen}
            onClose={onClose}
            title="Edit Profile"
            size="md"
            preventClose={loading || isUploading}
            footer={
                <>
                    <button
                        type="button"
                        className="btn-secondary"
                        onClick={onClose}
                        disabled={loading || isUploading}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        className="btn-primary"
                        onClick={() => handleSubmit()}
                        disabled={loading || isUploading}
                    >
                        {loading ? 'Saving...' : 'Save Changes'}
                    </button>
                </>
            }
        >
            <form onSubmit={handleSubmit} className="profile-form">
                <div className="profile-form-body">
                    <div className="avatar-upload-section">
                        <div className="avatar-preview-container">
                            {preview ? (
                                <img src={preview.startsWith('blob') || preview.startsWith('http') ? preview : (preview.startsWith('/storage') ? `http://localhost:8648${preview}` : `http://localhost:8648/storage/${preview}`)} alt="Preview" className="avatar-preview-img" />
                            ) : (
                                <div className="avatar-placeholder">
                                    {formData.name.charAt(0) || 'U'}
                                </div>
                            )}
                            {isUploading && (
                                <div className="avatar-upload-overlay">
                                    <div className="spinner-small"></div>
                                </div>
                            )}
                            <label htmlFor="avatar-input" className={`avatar-edit-badge ${isUploading ? 'disabled' : ''}`}>
                                <FaCamera />
                            </label>
                            <input
                                type="file"
                                id="avatar-input"
                                hidden
                                accept="image/*"
                                onChange={handleFileChange}
                            />
                        </div>
                        <p className="upload-hint">Upload image (Max 10MB)</p>
                        {errors.avatar_id && <span className="error-text">{errors.avatar_id[0]}</span>}
                    </div>

                    <div className="form-row">
                        <div className="form-group flex-1">
                            <label>Name</label>
                            <input
                                type="text"
                                className={`form-input ${errors.name ? 'is-invalid' : ''}`}
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                placeholder="Full Name"
                            />
                            {errors.name && <span className="error-text">{errors.name[0]}</span>}
                        </div>

                        <div className="form-group flex-1">
                            <label>Role</label>
                            <select
                                className="form-input"
                                value={formData.user_type}
                                onChange={(e) => setFormData({ ...formData, user_type: e.target.value })}
                            >
                                <option value="CEO">CEO</option>
                                <option value="Manager">Manager</option>
                                <option value="Chairperson">Chairperson</option>
                            </select>
                            {errors.user_type && <span className="error-text">{errors.user_type[0]}</span>}
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group flex-1">
                            <label>New Password (Optional)</label>
                            <input
                                type="password"
                                className={`form-input ${errors.password ? 'is-invalid' : ''}`}
                                value={formData.password}
                                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                placeholder="Min 8 characters"
                            />
                            {errors.password && <span className="error-text">{errors.password[0]}</span>}
                        </div>

                        <div className="form-group flex-1">
                            <label>Confirm Password</label>
                            <input
                                type="password"
                                className={`form-input`}
                                value={formData.password_confirmation}
                                onChange={(e) => setFormData({ ...formData, password_confirmation: e.target.value })}
                                placeholder="Repeat password"
                            />
                        </div>
                    </div>

                </div>
            </form>
        </AppModal>
    );
};

export default ProfileModal;
